import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftDetailsPanel = ({ 
  selectedShift, 
  onClose, 
  onBookShift, 
  onCancelShift, 
  onRequestShift 
}) => {
  if (!selectedShift) {
    return (
      <div className="w-80 bg-card border-l border-border p-6 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Wählen Sie eine Schicht aus, um Details anzuzeigen
          </p>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return {
      date: date?.toLocaleDateString('de-DE', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date?.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      assigned: {
        label: 'Zugewiesen',
        color: 'text-blue-700 bg-blue-50',
        icon: 'CheckCircle'
      },
      available: {
        label: 'Verfügbar',
        color: 'text-green-700 bg-green-50',
        icon: 'Plus'
      },
      requested: {
        label: 'Angefragt',
        color: 'text-yellow-700 bg-yellow-50',
        icon: 'Clock'
      },
      unavailable: {
        label: 'Nicht verfügbar',
        color: 'text-gray-700 bg-gray-50',
        icon: 'X'
      }
    };
    return statusMap?.[status] || statusMap?.unavailable;
  };

  const startDateTime = formatDateTime(selectedShift?.startTime);
  const endDateTime = formatDateTime(selectedShift?.endTime);
  const statusInfo = getStatusInfo(selectedShift?.status);

  const calculateDuration = () => {
    const start = new Date(selectedShift.startTime);
    const end = new Date(selectedShift.endTime);
    const diffHours = (end - start) / (1000 * 60 * 60);
    return `${diffHours} Stunden`;
  };

  const canBookShift = selectedShift?.status === 'available';
  const canCancelShift = selectedShift?.status === 'assigned' || selectedShift?.status === 'requested';
  const canRequestShift = selectedShift?.status === 'available';

  return (
    <div className="w-80 bg-card border-l border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Schicht-Details</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>
      </div>
      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Status Badge */}
        <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${statusInfo?.color}`}>
          <Icon name={statusInfo?.icon} size={16} className="mr-2" />
          {statusInfo?.label}
        </div>

        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Abteilung</label>
            <p className="text-lg font-semibold text-foreground">{selectedShift?.department}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Position</label>
            <p className="text-foreground">{selectedShift?.position}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Standort</label>
            <p className="text-foreground">{selectedShift?.location}</p>
          </div>
        </div>

        {/* Time Details */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Datum</label>
            <p className="text-foreground">{startDateTime?.date}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Beginn</label>
              <p className="text-foreground font-medium">{startDateTime?.time}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Ende</label>
              <p className="text-foreground font-medium">{endDateTime?.time}</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Dauer</label>
            <p className="text-foreground">{calculateDuration()}</p>
          </div>
        </div>

        {/* Additional Info */}
        {selectedShift?.requirements && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">Anforderungen</label>
            <p className="text-sm text-foreground mt-1">{selectedShift?.requirements}</p>
          </div>
        )}

        {selectedShift?.notes && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">Notizen</label>
            <p className="text-sm text-foreground mt-1">{selectedShift?.notes}</p>
          </div>
        )}

        {/* Conflict Warning */}
        {selectedShift?.hasConflict && (
          <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">Konflikt erkannt</p>
                <p className="text-xs text-warning/80 mt-1">
                  Diese Schicht überschneidet sich mit einer anderen Ihrer Schichten.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2 pt-4 border-t border-border">
          {canBookShift && (
            <Button
              variant="default"
              onClick={() => onBookShift(selectedShift)}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              Schicht buchen
            </Button>
          )}

          {canRequestShift && selectedShift?.requiresApproval && (
            <Button
              variant="outline"
              onClick={() => onRequestShift(selectedShift)}
              iconName="Clock"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              Schicht anfragen
            </Button>
          )}

          {canCancelShift && (
            <Button
              variant="destructive"
              onClick={() => onCancelShift(selectedShift)}
              iconName="X"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              {selectedShift?.status === 'assigned' ? 'Schicht stornieren' : 'Anfrage zurückziehen'}
            </Button>
          )}

          <Button
            variant="ghost"
            onClick={() => window.location.href = '/available-shifts-booking'}
            iconName="ExternalLink"
            iconPosition="left"
            iconSize={16}
            className="w-full"
          >
            Alle verfügbaren Schichten
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShiftDetailsPanel;